/**
 * useAuditEvents — TanStack Query hook for the Audit page (TECH-28).
 *
 * Server-paginated UNION of action_calculations (engine) + actions_acknowledge
 * (user). Filters mirror the backend query params 1:1 so URL state can be
 * serialized directly into the request. Bad filter values are normalised
 * client-side before the request goes out — empty strings collapse to
 * undefined so the URL doesn't carry noise.
 */
import { useQuery, keepPreviousData } from '@tanstack/react-query'
import client from '../api/client.js'

function compact(obj) {
  const out = {}
  for (const [k, v] of Object.entries(obj)) {
    if (v === undefined || v === null || v === '' || v === 'all') continue
    out[k] = v
  }
  return out
}

/**
 * @param {object} filters
 * @param {string} [filters.actionId]
 * @param {'engine'|'user'} [filters.source]
 * @param {string} [filters.eventType]
 * @param {string} [filters.userEmail]
 * @param {string} [filters.since]    — ISO 8601
 * @param {string} [filters.until]    — ISO 8601
 * @param {number} [filters.page]
 * @param {number} [filters.pageSize]
 */
export function useAuditEvents({
  actionId,
  source,
  eventType,
  userEmail,
  since,
  until,
  nodeType,
  nodeId,
  category,
  page = 1,
  pageSize = 50,
} = {}) {
  const params = compact({
    action_id: actionId,
    source,
    event_type: eventType,
    user_email: userEmail,
    since,
    until,
    node_type: nodeType,
    node_id: nodeId,
    category,
    page,
    page_size: pageSize,
  })

  return useQuery({
    queryKey: ['audit', 'actions', 'list', params],
    queryFn: async () => {
      const { data } = await client.get('/audit/actions', { params })
      return data
    },
    staleTime: 30 * 1000,
    // Keep the previous page visible while a new page loads — avoids the
    // empty-state flash on pagination clicks.
    placeholderData: keepPreviousData,
  })
}
